import { selectIsFeatureFlagVariation } from '@noths/polaris-client-user-configuration';
import { createSelector } from '@reduxjs/toolkit';

import { SORT_FILTER_UID } from 'src/constants/elementIds';
import { FacetedSearch } from 'src/constants/featureFlags';
import { ReduxApplicationState } from 'src/redux/combinedReducer';
import { Filter } from 'src/types';
import { getFilterCount, getFilterOptionCount, getFilterOptionsForFilter } from './selectorHelpers';

export interface FilterWithFilterOptionsCount extends Filter {
  selectedFilterOptionsCount: number;
}

export const selectSelectedFilterOptions = (state: ReduxApplicationState) =>
  state.filter.collections.selectedFilterOptions;

export const selectSelectedFilterOptionsWithoutSortFilter = (state: ReduxApplicationState) =>
  state.filter.collections.selectedFilterOptions.filter(
    (filter) => filter.filterUid != SORT_FILTER_UID,
  );

export const selectFilters = (state: ReduxApplicationState) => state.filter.collections.filters;

const selectFilter = (_state: ReduxApplicationState, filter?: Filter) => filter;

export const selectFiltersWithFilterOptionsCount = createSelector(
  [selectSelectedFilterOptions, selectFilters],
  (selectedFilterOptions, filters): FilterWithFilterOptionsCount[] =>
    filters.map((filter) => {
      const filterOptionsForFilter = getFilterOptionsForFilter(filter, selectedFilterOptions);
      const selectedFilterOptionsCount = filterOptionsForFilter.reduce(
        (count, option) => (count += getFilterOptionCount(option)),
        0,
      );

      return {
        ...filter,
        selectedFilterOptionsCount,
      };
    }),
);

export const selectFiltersWithFilterOptionsCountWithoutSortFilter = createSelector(
  [selectFiltersWithFilterOptionsCount],
  (filters): FilterWithFilterOptionsCount[] =>
    filters.filter((filter) => filter.uid != SORT_FILTER_UID),
);

export const selectFilterWithFilterOptionsCount = createSelector(
  [selectFiltersWithFilterOptionsCount, selectFilter],
  (selectedFiltersWithFilterOptionsCount, filter) =>
    selectedFiltersWithFilterOptionsCount.find((flt) => flt.uid === filter?.uid),
);

export const selectTotalSelectedFilterOptionsCount = createSelector(
  [selectSelectedFilterOptions],
  (selectedFilterOptions) =>
    selectedFilterOptions.reduce((count, option) => (count += getFilterOptionCount(option)), 0),
);

export const selectTotalFiltersAppliedCount = createSelector(
  [selectSelectedFilterOptions],
  (selectedFilterOptions) =>
    selectedFilterOptions.reduce((count, option) => (count += getFilterCount(option)), 0),
);

export const selectUserAppliedFilters = createSelector(
  [selectSelectedFilterOptions, selectFilters],
  (selectedFilterOptions, filters) =>
    selectedFilterOptions.reduce<Filter[]>(
      (userAppliedFilters, { filterUid, selectedByDefault }) => {
        const filter = filters.find(({ uid }) => uid === filterUid);

        if (selectedByDefault || !filter) {
          return userAppliedFilters;
        }

        return [...userAppliedFilters, filter];
      },
      [],
    ),
);

export const selectUserAppliedFilterTitles = createSelector([selectUserAppliedFilters], (filters) =>
  filters.reduce(
    (titles, filter) => (titles.length > 0 ? `${titles}, ${filter.title}` : `${filter.title}`),
    '',
  ),
);

export const selectIsUsingCollapsibleFiltersFeature = (state: ReduxApplicationState) =>
  selectIsFeatureFlagVariation(state, FacetedSearch.NAME, FacetedSearch.VARIANT_FACETED_SEARCH);
